import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
    Box,
    Typography,
    FormHelperText,
    Card,
    Container,
    useMediaQuery,
    useTheme,
    CircularProgress,
    Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CustomAppBar from "../../components/CustomAppBar";
import { callBackendApiPost } from "../../lib/api";
import logDrtEvent from "../../lib/logging";
import useGA4Event from "../../hooks/useGA4Event";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { alertOps } from "../../lib/api";
import { CheckCircle, Video, Calendar, Clock, Info } from 'lucide-react';

dayjs.extend(utc);
dayjs.extend(timezone);

export default function AppointmentConfirm() {
    const [processing, setProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const sendGA4Event = useGA4Event();

    const patient = useSelector((state) => state.user.selectedAppointmentPatient);
    const appointmentToReschedule = useSelector((state) => state.user.appointmentToReschedule);
    const selectedSlot = useSelector((state) => state.user.selectedSlot);
    const user = useSelector((state) => state.user);

    const variant = localStorage.getItem('ab_variant');
    const showApptBooking = variant === 'show_appt_booking_in_ret_intake';

    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

    useEffect(() => {
        if (!selectedSlot) {
            setErrorMessage("No appointment slot selected. Please go back and choose a time.");
        }
    }, [selectedSlot]);

    useEffect(() => {
        if (!patient) {
            setErrorMessage("Patient information is missing. Please return to the previous page.");
        }
    }, [patient]);

    const handleSubmitAppointment = async () => {
        if (!selectedSlot?.datetime || !selectedSlot?.clinicianID || !patient?.id) {
            setErrorMessage("Missing appointment or patient information.");
            return;
        }

        try {
            setProcessing(true);
            setErrorMessage("");

            const payload = {
                patientID: patient.id,
                tellescope_appointment_time: selectedSlot.datetime,
                tellescope_clinician_id: selectedSlot.clinicianID,
                ...(appointmentToReschedule?.id && {
                    reschedule: true,
                    reschedule_appointment_id: appointmentToReschedule.id,
                }),
            };

            const response = await callBackendApiPost("/api/v1/book-appointment", payload);

            if (response?.ok || response?.success) {
                if (user.portal) {
                    navigate('/appointment-list', { state: { viaBooking: true } });
                } else {
                    if (showApptBooking) {
                        navigate("/returning-appointment-thank-you");
                    } else {
                        navigate("/appointment-thank-you");
                    }
                }
            } else {
                await alertOps(user, "Appointment booking", response?.error, "handleSubmitAppointment");
                setErrorMessage("Unable to confirm your appointment. Please try again.");
                setProcessing(false);
            }
        } catch (err) {
            await alertOps(user, "Appointment booking", err, "handleSubmitAppointment");
            logDrtEvent(`Appointment booking error: ${err}`);
            setErrorMessage("An unexpected error occurred. Please try again later.");
            setProcessing(false);
        }
    };

    const containerStyle = {
        maxWidth: isDesktop ? "600px" : "100%",
        mx: "auto",
    };

    const formatAppointmentDateTime = () => {
        if (!selectedSlot?.datetime) return '';
        // Parse as UTC and convert to local timezone for display
        const date = dayjs.utc(selectedSlot.datetime).local();
        return date.format('MMM D, YYYY [at] h:mm A');
    };

    const appointmentType = appointmentToReschedule?.appointment_type 
        || selectedSlot?.appointment_type 
        || appointmentToReschedule?.appointmentType
        || selectedSlot?.appointmentType
        || 'Video Visit';

    if (!selectedSlot) {
        return (
            <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9fafb' }}>
                <Typography color="error">{errorMessage || "No appointment slot selected."}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f9fafb', pb: (theme) => `calc(${theme.spacing(6)} + ${theme.spacing(4)})`, }}>
            <CustomAppBar noBack={false} returning={false} portal={false} />
            <Container sx={{ maxWidth: isDesktop ? '480px' : '100%', px: isDesktop ? 4 : 3, pt: 4 }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                    <Box
                        sx={{
                            backgroundColor: '#d1fae5',
                            borderRadius: '8px',
                            p: 1.25,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <CheckCircle size={20} color="#059669" />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontFamily: 'Sentient, serif',
                                fontWeight: 500,
                                color: '#111827',
                                fontSize: '1.28rem',
                                mb: 0.5,
                            }}
                        >
                            Confirm Your Appointment
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#6b7280',
                                fontSize: '0.95rem',
                            }}
                        >
                            Please review the details below and confirm your appointment
                        </Typography>
                    </Box>
                </Box>

                {/* Appointment Details Card */}
                <Card
                    elevation={0}
                    sx={{
                        mb: 2,
                        background: 'linear-gradient(to bottom right, rgba(239, 246, 255, 0.5), rgba(236, 254, 255, 0.3))',
                        borderRadius: '12px',
                        border: '1px solid rgba(219, 234, 254, 1)',
                        p: 2.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Appointment Type */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Video size={18} color="#6b7280" />
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#9ca3af',
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        mb: 0.25,
                                        display: 'block',
                                    }}
                                >
                                    APPOINTMENT TYPE
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: '#111827',
                                        fontSize: '0.95rem',
                                        fontWeight: 700,
                                    }}
                                >
                                    {appointmentType}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Appointment Time */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Calendar size={18} color="#6b7280" />
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#9ca3af',
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        mb: 0.25,
                                        display: 'block',
                                    }}
                                >
                                    APPOINTMENT TIME
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: '#111827',
                                        fontSize: '0.95rem',
                                        fontWeight: 700,
                                    }}
                                >
                                    {formatAppointmentDateTime()}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Duration */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Clock size={18} color="#6b7280" />
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#9ca3af',
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        mb: 0.25,
                                        display: 'block',
                                    }}
                                >
                                    DURATION
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: '#111827',
                                        fontSize: '0.95rem',
                                        fontWeight: 700,
                                    }}
                                >
                                    15 min
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Card>

                {/* What to Expect Card */}
                <Card
                    elevation={0}
                    sx={{
                        mb: 3,
                        background: 'linear-gradient(to bottom right, rgba(240, 253, 250, 0.6), rgba(236, 253, 245, 0.4))',
                        borderRadius: '12px',
                        border: '1px solid rgba(204, 251, 241, 1)',
                        p: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box
                            sx={{
                                backgroundColor: '#d1fae5',
                                borderRadius: '50%',
                                p: 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <Info size={14} color="#059669" />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    color: '#111827',
                                    fontSize: '0.95rem',
                                    mb: 0.75,
                                }}
                            >
                                What to Expect
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#374151',
                                    fontSize: '0.875rem',
                                    lineHeight: 1.5,
                                }}
                            >
                                Your video link will be sent via email and text, and we’ll also send reminders before your appointment.
                            </Typography>
                        </Box>
                    </Box>
                </Card>
            </Container>

            {/* Confirm Appointment Button - Sticky Footer */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    borderTop: '1px solid #e5e7eb',
                    p: 2,
                    boxShadow: 'none',
                    zIndex: 1000,
                }}
            >
                <Container sx={{ maxWidth: isDesktop ? '480px' : '100%', px: isDesktop ? 4 : 3 }}>
                    <Button
                        variant="contained"
                        startIcon={processing ? <CircularProgress size={18} color="inherit" /> : <CheckCircle size={20} color="white" />}
                        onClick={() => {
                            sendGA4Event('appointment_confirm_button_click', {
                                'button_id': 'confirm_appointment',
                                'button_text': 'Confirm Appointment',
                                'is_reschedule': !!appointmentToReschedule,
                                'patient_id': patient?.id || 'unknown',
                                'appointment_type': appointmentType,
                                'appointment_datetime': selectedSlot?.datetime || 'unknown',
                            });
                            handleSubmitAppointment();
                        }}
                        disabled={processing || !selectedSlot}
                        fullWidth
                        sx={{
                            width: '100%',
                            background: 'linear-gradient(to right, #003B5C, #004C73)',
                            color: 'white',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            height: '48px',
                            boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                            '&:hover': {
                                background: 'linear-gradient(to right, #00294A, #003B5C)',
                            },
                            '&:disabled': {
                                backgroundColor: '#9ca3af',
                                background: '#9ca3af',
                            },
                        }}
                    >
                        {processing ? 'Processing...' : 'Confirm Appointment'}
                    </Button>
                    {errorMessage && (
                        <FormHelperText error sx={{ fontSize: '0.85rem', mt: 1, textAlign: 'center' }}>
                            {errorMessage}
                        </FormHelperText>
                    )}
                </Container>
            </Box>
        </Box>
    );
}
